import type { AgChartOptions } from 'ag-charts-community'

import type { AccruedInterests } from '@fortem-monorepo/web-sdk'

const dateLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
const atDate = (date: string) => new Date(`${date.slice(0, 10)}T00:00:00Z`)
const axisAmount = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

/** Plot approximate coordinates with exact tooltips and a separate nonfinancial event axis. */
export const getBalanceChartOptions = (history: AccruedInterests.BalanceChart): AgChartOptions => ({
  background: { visible: false },
  legend: { position: 'bottom' },
  zoom: { enabled: true },
  axes: [
    {
      type: 'time',
      position: 'bottom',
      label: { formatter: ({ value }) => dateLabel.format(new Date(value)) },
      crossLines: history.coupon_dates.map((date) => ({
        type: 'line',
        value: atDate(date),
        stroke: '#eab308',
        strokeWidth: 3,
        strokeOpacity: 0.75,
      })),
    },
    {
      type: 'number',
      position: 'left',
      keys: ['balance'],
      title: { text: `Closing accrued balance (${history.instrument.currency ?? 'local'})` },
      label: { formatter: ({ value }) => axisAmount.format(Number(value)) },
    },
    {
      type: 'number',
      position: 'right',
      keys: ['event_level'],
      min: -0.5,
      max: 1.5,
      label: { enabled: false },
      tick: { enabled: false },
      line: { enabled: false },
      gridLine: { enabled: false },
    },
  ],
  series: [
    {
      type: 'line',
      xKey: 'date',
      yKey: 'balance',
      yName: 'Closing accrued balance',
      stroke: '#2563eb',
      connectMissingData: false,
      data: history.points.map((point) => ({
        date: atDate(point.value_date),
        // Plot coordinates are approximate; keep the exact decimal for tooltip/export.
        balance: point.accrued_balance_local == null ? undefined : Number(point.accrued_balance_local),
        amount: point.accrued_balance_local,
        trade_count: point.trade_count,
      })),
      tooltip: {
        renderer: ({ datum }) => ({
          heading: dateLabel.format(datum.date),
          title: 'Closing accrued balance',
          data: [
            { label: history.instrument.currency ?? 'Local', value: datum.amount ?? 'Unknown' },
            { label: 'Saved trade balances', value: String(datum.trade_count) },
          ],
        }),
      },
    },
    ...(['BUY', 'SELL'] as const).map((side) => ({
      type: 'scatter' as const,
      xKey: 'date',
      yKey: 'event_level',
      yName: side === 'BUY' ? 'Buy' : 'Sell',
      shape: 'triangle' as const,
      size: 12,
      fill: side === 'BUY' ? '#15803d' : '#b91c1c',
      stroke: side === 'BUY' ? '#166534' : '#991b1b',
      data: history.trades
        .filter((trade) => trade.side === side)
        .map((trade) => ({
          ...trade,
          date: atDate(trade.trade_datetime_utc),
          event_level: side === 'BUY' ? 1 : 0,
        })),
      tooltip: {
        renderer: ({ datum }: { datum: AccruedInterests.ChartTrade }) => ({
          heading: dateLabel.format(atDate(datum.trade_datetime_utc)),
          title: side === 'BUY' ? 'Buy' : 'Sell',
          data: [
            { label: 'Trade', value: datum.trade_id },
            { label: 'Quantity', value: datum.quantity },
          ],
        }),
      },
    })),
  ],
})
